import type { WorkspaceTask } from '../../store/api/workspaceApi';

export type WorkspaceTaskActionKind =
  | 'openDocuments'
  | 'openFinance'
  | 'openDispatch'
  | 'openOriginalsSent'
  | 'openConfirmReceipt'
  | 'sendInvoiceToClient';

export type ApplicationTaskTarget = {
  tab: 'finance' | 'documents';
  action?:
    | 'create-invoice'
    | 'confirm-invoice-sent'
    | 'upload-loading-docs'
    | 'upload-unloading-docs'
    | 'upload-upd'
    | 'confirm-upd-sent';
};

const DOCUMENT_TASK_TYPES = new Set([
  'LOADING_PHOTO',
  'LOADING_DOCUMENTS_UPLOAD',
  'UPLOAD_LOADING_PHOTO',
  'UNLOADING_PHOTO',
  'UNLOADING_DOCUMENTS_UPLOAD',
  'NO_DOCS_UNLOADING',
  'TN_ORIGINAL_MISSING',
  'UPLOAD_TN_ORIGINAL',
  'CARRIER_INVOICE_MISSING',
  'UPD_UPLOAD',
  'UPD_CONFIRM',
  'INVOICE_EXPECTED',
  'SEND_ORIGINAL_TN',
]);

const FINANCE_TASK_TYPES = new Set([
  'PAYMENT_EXPECTED',
  'EXPENSE_CONFIRMATION',
  'CARRIER_INVOICE_CONFIRM',
  'CREATE_CLIENT_INVOICE',
  'CONFIRM_INVOICE_SENT',
  'CLIENT_PAYMENT_DUE',
]);

const DISPATCH_TASK_TYPES = new Set([
  'CLIENT_DOCS_SENT',
  'MISSING_CARRIER',
  'ADD_CARRIER',
  'MISSING_PRICE',
  'MISSING_RESPONSIBLE',
  'MISSING_CONTRACT',
  'UPLOAD_LOADING_DOCS',
  'UPLOAD_UNLOADING_DOCS',
  'UPLOAD_UPD',
  'CONFIRM_UPD_SENT',
]);

const ORIGINALS_SENT_TASK_TYPES = new Set([
  'ORIGINALS_SENT',
]);

const CONFIRM_RECEIPT_TASK_TYPES = new Set([
  'CLIENT_DOCS_RECEIVED',
  'CONFIRM_RECEIPT',
]);

const SEND_INVOICE_TASK_TYPES = new Set([
  'SEND_INVOICE_TO_CLIENT',
  'SEND_INVOICE',
]);

export function getWorkspaceTaskActionKind(taskType: string | null | undefined): WorkspaceTaskActionKind | null {
  const normalized = String(taskType || '').toUpperCase();

  if (DOCUMENT_TASK_TYPES.has(normalized)) return 'openDocuments';
  if (FINANCE_TASK_TYPES.has(normalized)) return 'openFinance';
  if (DISPATCH_TASK_TYPES.has(normalized)) return 'openDispatch';
  if (ORIGINALS_SENT_TASK_TYPES.has(normalized)) return 'openOriginalsSent';
  if (CONFIRM_RECEIPT_TASK_TYPES.has(normalized)) return 'openConfirmReceipt';
  if (SEND_INVOICE_TASK_TYPES.has(normalized)) return 'sendInvoiceToClient';

  return null;
}

export function canHandleWorkspaceTaskAction(
  task: Pick<WorkspaceTask, 'taskType' | 'routeId' | 'applicationId' | 'invoiceId' | 'entityType'>
): boolean {
  const actionKind = getWorkspaceTaskActionKind(task.taskType);
  const isApplicationTask = String(task.entityType || '').toUpperCase() === 'APPLICATION';

  if (!actionKind) return false;
  if (isApplicationTask) return Boolean(task.applicationId);
  if (actionKind === 'sendInvoiceToClient') return Boolean(task.invoiceId);

  return Boolean(task.routeId || task.applicationId);
}

export function getApplicationTaskTarget(taskType: string | null | undefined): ApplicationTaskTarget | null {
  const normalized = String(taskType || '').toUpperCase();

  if (normalized === 'CREATE_CLIENT_INVOICE') {
    return { tab: 'finance', action: 'create-invoice' };
  }

  if (normalized === 'CONFIRM_INVOICE_SENT') {
    return { tab: 'finance', action: 'confirm-invoice-sent' };
  }

  if (normalized === 'UPLOAD_LOADING_DOCS') {
    return { tab: 'documents', action: 'upload-loading-docs' };
  }

  if (normalized === 'UPLOAD_UNLOADING_DOCS') {
    return { tab: 'documents', action: 'upload-unloading-docs' };
  }

  if (normalized === 'UPLOAD_UPD') {
    return { tab: 'documents', action: 'upload-upd' };
  }

  if (normalized === 'CONFIRM_UPD_SENT') {
    return { tab: 'documents', action: 'confirm-upd-sent' };
  }

  return null;
}
